import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { AdminApi, AdminClassroom, AdminCourse, AdminRegistrationFilter, AdminRegistrationItem } from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const statusOptions = ['', 'TEST_PAYMENT', 'WAIT_PAYMENT', 'SUCCESS', 'CANCEL', 'LEARNING', 'RESERVE', 'FINISH']

const formatMoney = (v?: number) => (v == null ? '-' : `${v.toLocaleString('vi-VN')} đ`)

const formatCourseMethodLabel = (method?: string) => {
  const u = String(method || '')
    .trim()
    .toUpperCase()
  const map: Record<string, string> = {
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    VIDEO: 'Video',
    HYBRID: 'Kết hợp'
  }
  return map[u] || (u ? u : '-')
}

const registrationStatusChipClass = (status: string) => {
  switch (status) {
    case 'SUCCESS':
      return 'bg-m2-secondary/15 text-green-800'
    case 'WAIT_PAYMENT':
      return 'bg-m2-primary/15 text-m2-primary'
    case 'CANCEL':
      return 'bg-red-100 text-m2-error'
    case 'LEARNING':
      return 'bg-indigo-100 text-indigo-800'
    case 'RESERVE':
      return 'bg-orange-100 text-orange-300'
    case 'FINISH':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-m2-surface-high text-m2-on-surface'
  }
}

const AdminRegistrationListPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminRegistrationItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [filters, setFilters] = useState<AdminRegistrationFilter>({
    registration_scope: 'all',
    code: '',
    email: '',
    phone: '',
    full_name: '',
    course: '',
    classroom: ''
  })
  const [status, setStatus] = useState<string[]>([])
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [classrooms, setClassrooms] = useState<AdminClassroom[]>([])
  const [fulfillingId, setFulfillingId] = useState<number | null>(null)
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await AdminApi.getRegistrations({
        ...filters,
        status: status[0] || undefined,
        page,
        limit: 20
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
    } catch (e) {
      toast.error('Không tải được danh sách đăng ký')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    ; (async () => {
      try {
        const [courseRes, classroomRes] = await Promise.all([AdminApi.getCourses(), AdminApi.getClassrooms()])
        setCourses(courseRes.data.items)
        setClassrooms(classroomRes.data.items)
      } catch {
        toast.error('Không tải được dữ liệu combobox')
      }
    })()
  }, [])

  useEffect(() => {
    ; (async () => {
      try {
        const selectedCourseId = filters.course_id
        const res = await AdminApi.getClassrooms(selectedCourseId)
        setClassrooms(res.data.items)
      } catch {
        toast.error('Không tải được danh sách lớp')
      }
    })()
  }, [filters.course_id])

  const applyFilters = () => {
    setPage(1)
    loadData()
  }

  const canPublish = (row: AdminRegistrationItem) =>
    !row.isGift && row.status === 'SUCCESS' && row.misaInvoiceStatus !== 'SUCCESS'

  const handlePublish = async (row: AdminRegistrationItem) => {
    try {
      await AdminApi.publishMisaForRegistration(row.id)
      toast.success(`Đã gửi yêu cầu xuất hóa đơn cho ${row.code}`)
      loadData()
    } catch (e) {
      toast.error('Xuất hóa đơn thất bại')
    }
  }

  const canConfirmFulfillment = (row: AdminRegistrationItem) =>
    !row.isGift && row.status === 'WAIT_PAYMENT'
  const canCancelWaitPayment = (row: AdminRegistrationItem) =>
    !row.isGift && row.status === 'WAIT_PAYMENT'

  const handleConfirmFulfillment = async (row: AdminRegistrationItem) => {
    if (
      !window.confirm(
        `Xác nhận thanh toán cho mã ${row.code}? Hệ thống sẽ chuyển sang SUCCESS, share Drive, gửi email học viên và xuất hóa đơn (nếu cần).`
      )
    )
      return
    setFulfillingId(row.id)
    try {
      const res = await AdminApi.confirmRegistrationFulfillment(row.id)
      if (res.success) {
        toast.success(res.message || 'Đã xử lý xác nhận đăng ký')
        loadData()
      } else {
        toast.error(res.message || 'Thao tác thất bại')
      }
    } catch {
      toast.error('Không gọi được API xác nhận')
    } finally {
      setFulfillingId(null)
    }
  }

  const handleCancelWaitPayment = async (row: AdminRegistrationItem) => {
    if (
      !window.confirm(
        `Hủy đăng ký ${row.code}? Trạng thái sẽ là CANCEL, sĩ số lớp/khóa giảm 1 và email thông báo gửi tới học viên.`
      )
    )
      return
    setCancellingId(row.id)
    try {
      const res = await AdminApi.cancelWaitPaymentRegistration(row.id)
      if (res.success) {
        toast.success(res.message || 'Đã hủy đăng ký')
        loadData()
      } else {
        toast.error(res.message || 'Hủy thất bại')
      }
    } catch {
      toast.error('Không gọi được API hủy')
    } finally {
      setCancellingId(null)
    }
  }

  const rows = useMemo(() => items, [items])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-m2-outline-variant pb-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-m2-primary border-b-2 border-m2-primary pb-1">
              Danh sách đăng ký cá nhân
            </span>
            <span className="text-m2-outline">|</span>
            <Link
              to="/admin/enterprise-batches"
              className="text-lg font-medium text-m2-on-surface-variant hover:text-m2-primary"
            >
              Lô đăng ký doanh nghiệp
            </Link>
          </div>
          <p className="text-sm text-m2-on-surface-variant mt-1">
            Tra cứu tất cả đăng ký học viên, xác nhận thanh toán hoặc thao tác đơn lẻ.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/enterprise-batches"
            className="inline-flex items-center gap-1 rounded-lg border border-m2-primary px-3 py-2 text-sm font-medium text-m2-primary hover:bg-m2-primary/10 transition-colors"
          >
            Quản lý lô DN & Xuất HĐ tổng
          </Link>
          <Link to="/admin/enterprise-registrations/new" className="btn-primary">
            Đăng ký khóa học cho DN
          </Link>
        </div>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={filters.registration_scope ?? 'all'}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                registration_scope: e.target.value as NonNullable<AdminRegistrationFilter['registration_scope']>
              }))
            }
          >
            <option value="all">Tất cả</option>
            <option value="enterprise">Doanh nghiệp</option>
            <option value="individual">Cá nhân</option>
          </select>
          <input className="rounded border border-m2-outline px-3 py-2 text-base" placeholder="Mã đăng ký" value={filters.code} onChange={(e) => setFilters((p) => ({ ...p, code: e.target.value }))} />
          <input className="rounded border border-m2-outline px-3 py-2 text-base" placeholder="Email" value={filters.email} onChange={(e) => setFilters((p) => ({ ...p, email: e.target.value }))} />
          <input className="rounded border border-m2-outline px-3 py-2 text-base" placeholder="Số điện thoại" value={filters.phone} onChange={(e) => setFilters((p) => ({ ...p, phone: e.target.value }))} />
          <input className="rounded border border-m2-outline px-3 py-2 text-base" placeholder="Họ tên" value={filters.full_name} onChange={(e) => setFilters((p) => ({ ...p, full_name: e.target.value }))} />
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={filters.course_id ?? ''}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                course_id: e.target.value ? Number(e.target.value) : undefined,
                classroom_id: undefined
              }))
            }
          >
            <option value="">Tất cả khóa học</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name} {course.is_active ? '(ACTIVE)' : ''}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={filters.classroom_id ?? ''}
            onChange={(e) => setFilters((p) => ({ ...p, classroom_id: e.target.value ? Number(e.target.value) : undefined }))}
          >
            <option value="">Tất cả lớp học</option>
            {classrooms.map((classroom) => (
              <option key={classroom.id} value={classroom.id}>
                {classroom.code} - {classroom.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button className="rounded bg-m2-primary px-3 py-2 text-base text-white hover:bg-eh-forest-shadow" onClick={applyFilters}>
              Tìm
            </button>
            <button
              className="rounded border border-m2-outline px-3 py-2 text-base text-m2-on-surface hover:bg-m2-surface-low"
              onClick={() => {
                setFilters({ registration_scope: 'all', code: '', email: '', phone: '', full_name: '', course: '', classroom: '' })
                setStatus([])
                setPage(1)
                setTimeout(loadData, 0)
              }}
            >
              Xóa lọc
            </button>
          </div>
        </div>
        <div className="mt-3">
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={status[0] ?? ''}
            onChange={(e) => setStatus(e.target.value ? [e.target.value] : [])}
          >
            <option value="">Tất cả trạng thái</option>
            {statusOptions.filter(Boolean).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-base">
            <thead className="bg-m2-surface-low text-left text-sm uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Mã ĐK</th>
                <th className="px-3 py-2">Thời gian</th>
                <th className="px-3 py-2">Học viên</th>
                <th className="px-3 py-2">Khóa học</th>
                <th className="px-3 py-2">Lớp</th>
                <th className="px-3 py-2">Tiền</th>
                <th className="px-3 py-2">Doanh nghiệp</th>
                <th className="px-3 py-2">HĐ MISA</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={9}>
                    Đang tải...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={9}>
                    Không có dữ liệu
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-m2-outline-variant align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-m2-on-surface">{row.code}</div>
                      <span className={`mt-1 inline-flex rounded px-2 py-1 text-sm font-medium ${registrationStatusChipClass(row.status)}`}>
                        {row.status}
                      </span>
                      {row.isGift && (
                        <span className="mt-1 ml-1 inline-flex rounded px-2 py-1 text-xs font-semibold bg-m2-surface-high text-m2-on-surface border border-m2-outline">
                          Tặng kèm
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-m2-on-surface-variant">
                      <div>ĐK: {formatDbDateTimeVi(row.registerAt)}</div>
                      <div>XN: {formatDbDateTimeVi(row.approvedAt)}</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-m2-on-surface">{row.user?.fullName || '-'}</div>
                      <div className="text-m2-on-surface-variant">{row.user?.email || '-'}</div>
                      <div className="text-m2-on-surface-variant">{row.user?.phone || '-'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-m2-on-surface">{row.course.code}</div>
                      <div className="text-m2-on-surface-variant">{formatCourseMethodLabel(row.course.method)}</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-m2-on-surface">{row.classroom.code}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-m2-on-surface">
                      {row.isGift ? (
                        <>
                          <div className="font-semibold text-amber-800">0 đ</div>
                          <div className="text-xs text-amber-900 mt-1">
                            Tặng kèm ĐK {row.parentRegisterCode || row.parentRegisterId || '-'}
                            {row.parentCourseName ? ` / ${row.parentCourseName}` : ''}
                          </div>
                        </>
                      ) : (
                        <>
                          <div>Gốc: {formatMoney(row.originalFee)}</div>
                          <div>KM: {formatMoney(row.discountAmount)}</div>
                          <div className="font-semibold">Trả: {formatMoney(row.fee)}</div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-m2-on-surface">{row.enterpriseName || '-'}</td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`rounded px-2 py-1 ${row.misaInvoiceStatus === 'SUCCESS'
                            ? 'bg-m2-secondary/15 text-green-700'
                            : row.misaInvoiceStatus === 'FAILED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-m2-surface-high text-m2-on-surface'
                          }`}
                      >
                        {row.misaInvoiceStatus || 'CHUA_XUAT'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          title="Chỉ WAIT_PAYMENT: xác nhận thanh toán → SUCCESS, share Drive, email, xuất HĐ nếu cần"
                          disabled={!canConfirmFulfillment(row) || fulfillingId === row.id || cancellingId === row.id}
                          onClick={() => handleConfirmFulfillment(row)}
                          className="inline-flex items-center gap-1 rounded border border-emerald-600 px-2 py-1 text-sm text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-m2-outline disabled:text-m2-outline"
                        >
                          <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden />
                          Xác nhận
                        </button>
                        <button
                          type="button"
                          title="Chỉ WAIT_PAYMENT: hủy đăng ký, trừ sĩ số, gửi email"
                          disabled={!canCancelWaitPayment(row) || fulfillingId === row.id || cancellingId === row.id}
                          onClick={() => handleCancelWaitPayment(row)}
                          className="inline-flex items-center gap-1 rounded border border-red-600 px-2 py-1 text-sm text-m2-error hover:bg-m2-error/10 disabled:cursor-not-allowed disabled:border-m2-outline disabled:text-m2-outline"
                        >
                          <XCircleIcon className="h-5 w-5 shrink-0" aria-hidden />
                          Hủy
                        </button>
                        <button
                          disabled={!canPublish(row)}
                          onClick={() => handlePublish(row)}
                          className="rounded border border-primary-600 px-2 py-1 text-sm text-m2-primary disabled:cursor-not-allowed disabled:border-m2-outline disabled:text-m2-outline"
                        >
                          Xuất HĐ
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button className="rounded border border-m2-outline px-3 py-1 text-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Trước
        </button>
        <span className="text-sm text-m2-on-surface-variant">
          Trang {page} / {totalPages || 1}
        </span>
        <button className="rounded border border-m2-outline px-3 py-1 text-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Sau
        </button>
      </div>
    </div>
  )
}

export default AdminRegistrationListPage

